import * as signalR from '@microsoft/signalr';
import { OriginatorTypes } from '@enums/originatorTypes';
import { RecipientTypes } from '@enums/recipientTypes';
const apiURL = import.meta.env.VITE_BASEURL.replace(/\/+$/, '');
const baseURL = apiURL.replace(/\/api$/, '');
const SIGNALR_URL = `${baseURL}/chatHub`;

export interface MessageObject {
  entityType: string;
  entityId: string;
  message: string;
  originatorType: OriginatorTypes;
  recipientType: RecipientTypes;
  sentTime: string;
}

class ChatHubConnector {
  static instance: ChatHubConnector;
  private connection: signalR.HubConnection;
  private isConnected = false;

  private connectionStateListeners: Set<() => void> = new Set();

  constructor() {
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(SIGNALR_URL, {
        withCredentials: true,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information)
      .build();

    // Listen to connection state changes
    this.connection.onclose(error => {
      this.isConnected = false;
      console.error('Chat Hub connection closed:', error);
      this.notifyConnectionStateListeners();
    });

    this.connection.onreconnecting(error => {
      this.isConnected = false;
      console.warn('Chat Hub reconnecting:', error);
      this.notifyConnectionStateListeners();
    });

    this.connection.onreconnected(() => {
      this.isConnected = true;
      this.notifyConnectionStateListeners();
    });

    // Start connection
    this.connection
      .start()
      .then(() => {
        this.isConnected = true;
        this.notifyConnectionStateListeners();
      })
      .catch(err => {
        console.error('Chat Hub connection error:', err);
        this.isConnected = false;
        this.notifyConnectionStateListeners();
      });

  }

  private notifyConnectionStateListeners(): void {
    this.connectionStateListeners.forEach(listener => listener());
  }

  public onConnectionStateChange(callback: () => void): () => void {
    this.connectionStateListeners.add(callback);
    // Return unsubscribe function
    return () => {
      this.connectionStateListeners.delete(callback);
    };
  }

  public static getInstance(): ChatHubConnector {
    if (!ChatHubConnector.instance) {
      ChatHubConnector.instance = new ChatHubConnector();
    }
    return ChatHubConnector.instance;
  }

  public async joinConversation(
    entityType: string,
    entityId: string
  ): Promise<void> {
    if (this.isConnected) {
      try {
        await this.connection.invoke('JoinEntityGroup', entityType, entityId);
      } catch (error) {
        console.error(
          'Error joining conversation:',
          entityType,
          entityId,
          error
        );
        throw error;
      }
    } else {
      console.error(
        'Hub not connected, cannot join conversation:',
        entityType,
        entityId
      );
      throw new Error('Hub not connected, cannot join conversation');
    }
  }

  public async leaveConversation(
    entityType: string,
    entityId: string
  ): Promise<void> {
    if (this.isConnected) {
      try {
        await this.connection.invoke('LeaveEntityGroup', entityType, entityId);
      } catch (error) {
        console.error(
          'Error leaving conversation:',
          entityType,
          entityId,
          error
        );
      }
    }
  }

  public async sendMessage(
    entityType: string,
    entityId: string,
    message: MessageObject
  ): Promise<void> {
    if (this.isConnected) {
      try {
        await this.connection.invoke(
          'SendMessage',
          entityType,
          entityId,
          message
        );
      } catch (error) {
        console.error(
          'Error sending message via chat hub:',
          entityType,
          entityId,
          message,
          error
        );
        throw error;
      }
    } else {
      console.warn(
        'Hub not connected, cannot send message:',
        entityType,
        entityId,
        message
      );
      throw new Error('Hub not connected');
    }
  }

  public onReceiveMessage(callback: (messageObj: MessageObject) => void): void {
    this.connection.on('ReceiveMessage', callback);
  }

  public offReceiveMessage(
    callback: (messageObj: MessageObject) => void
  ): void {
    this.connection.off('ReceiveMessage', callback);
  }

  public onUnreadCountUpdated(
    callback: (entityType: string, entityId: string, unread: number) => void
  ): void {
    this.connection.on('UnreadCountUpdated', callback);
  }

  public offUnreadCountUpdated(
    callback: (entityType: string, entityId: string, unread: number) => void
  ): void {
    this.connection.off('UnreadCountUpdated', callback);
  }

  public getConnectionState(): signalR.HubConnectionState {
    return this.connection.state;
  }

  public isHubConnected(): boolean {
    return (
      this.isConnected &&
      this.connection.state === signalR.HubConnectionState.Connected
    );
  }

  public getConnectionId(): string | null {
    return this.connection.connectionId;
  }

  public getConnection() {
    return this.connection;
  }

  public async disconnect(): Promise<void> {
    if (this.connection) {
      await this.connection.stop();
      this.isConnected = false;
    }
  }
}

export default ChatHubConnector.getInstance;
